/**
 * Token Service for fetching ephemeral tokens from backend
 * 
 * This service handles secure token generation by communicating with the backend
 * which locks sensitive configurations (system instructions, model, voice) in the token.
 */

export interface TokenRequest {
  instruction_type?: string;
  voice_name?: string;
}

export interface TokenResponse {
  token: string;
  model: string;
}

/**
 * Fetch an ephemeral token from the backend for Gemini Live API
 * 
 * @param backendUrl - The base URL of the backend API
 * @param instructionType - The type of system instruction to lock in (default: "property_evaluation")
 * @param voiceName - The voice name to use (optional, uses backend default if not specified)
 * @returns Promise with token and model information
 * @throws Error if token generation fails
 */
export async function fetchGeminiToken(
  backendUrl: string,
  instructionType: string = "property_evaluation",
  voiceName?: string
): Promise<TokenResponse> {
  console.log(`🔑 [TOKEN] Fetching ephemeral token from backend...`);
  console.log(`📋 [TOKEN] Instruction type: ${instructionType}`);
  if (voiceName) {
    console.log(`🎤 [TOKEN] Voice name: ${voiceName}`);
  }

  try {
    const requestBody: TokenRequest = {
      instruction_type: instructionType,
    };

    if (voiceName) {
      requestBody.voice_name = voiceName;
    }

    const response = await fetch(`${backendUrl}/api/get-gemini-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [TOKEN] Failed to fetch token: ${response.status} ${errorText}`);
      throw new Error(`Failed to fetch token: ${response.status} ${errorText}`);
    }

    const data: TokenResponse = await response.json();
    
    console.log(`✅ [TOKEN] Token fetched successfully`);
    console.log(`📋 [TOKEN] Model locked to: ${data.model}`);
    console.log(`🔑 [FRONTEND] Full Token: ${data.token}`);

    return data;
  } catch (error) {
    console.error(`❌ [TOKEN] Error fetching token:`, error);
    throw error;
  }
}

// Backend URL is now imported from centralized config
export { getBackendUrl } from "../config";

